import * as React from 'react';

import { enumQuoteType } from '@rematter/frontend-lib-api-client';
import { QuoteRecord } from '@rematter/modules/brokerage/types/quote';
import { colorByQuoteStatus } from '@rematter/modules/brokerage/utils/status';
import {
  Badge,
  DataGridColumn,
  formatMoney,
  LocalizedDate,
} from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';
import { QuoteActionMenu } from '../QuoteActionMenu';

type Props = {
  onRowClick: (row: QuoteRecord) => void;
  onVoid?: (quote: QuoteRecord) => void;
  onRefetch?: () => void;
};

export const useQuoteListColumns = ({ onRowClick, onVoid }: Props) => {
  const t = useTranslations();

  // Callbacks
  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: QuoteRecord }) => {
      onRowClick(row);
    },
    [onRowClick],
  );

  // Memoized values
  const columns: DataGridColumn<QuoteRecord>[] = React.useMemo(
    () => [
      {
        key: 'quoteNumber',
        header: t.quotes.table.columns.quoteNumber,
        sortable: true,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left">
            {row.quoteNumber}
          </div>
        ),
      },
      {
        key: 'type',
        header: t.quotes.table.columns.type,
        sortable: true,
        width: 100,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge
            color={row.type === enumQuoteType.Purchase ? 'warning' : 'success'}
            size="small"
          >
            {row.type === enumQuoteType.Purchase
              ? t.quotes.types.purchaseShort
              : t.quotes.types.salesShort}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: t.quotes.table.columns.status,
        sortable: true,
        width: 100,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge color={colorByQuoteStatus[row.status]} size="small">
            {
              t.common.status[
                row.status.toLowerCase() as keyof typeof t.common.status
              ]
            }
          </Badge>
        ),
      },
      {
        key: 'account',
        header: t.quotes.table.columns.account,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => <div className="text-left">{row.account.name}</div>,
      },
      {
        key: 'lineItemCount',
        header: t.quotes.table.columns.items,
        width: 80,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-center">{row.lineItemCount}</div>
        ),
      },
      {
        key: 'subtotal',
        header: t.quotes.table.columns.subtotal,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-right">
            {formatMoney(row.subtotal.amount, row.subtotal.currencyCode)}
          </div>
        ),
      },
      {
        key: 'validUntil',
        header: t.quotes.table.columns.validUntil,
        sortable: true,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => {
          if (!row.validUntil) return <div className="text-left">-</div>;
          return (
            <div className="text-left">
              <LocalizedDate date={row.validUntil} />
            </div>
          );
        },
      },
      {
        key: 'createdAt',
        header: t.quotes.table.columns.created,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => <QuoteActionMenu quote={row} onVoid={onVoid} />,
      },
    ],
    [t, handleCellDoubleClick, onVoid],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
